<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <!-- 背景遮罩 -->
      <div 
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="$emit('close')"
      ></div>

      <!-- 模态框内容 -->
      <div class="relative bg-white rounded-lg shadow-xl transform transition-all w-full max-w-lg max-h-[85vh] flex flex-col p-6">
        <!-- 头部 -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center">
            <div class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-green-100 mr-3">
              <!-- 微信图标 -->
              <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900">微信通知设置</h3>
          </div>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- 提示信息 -->
        <div class="mb-4">
          <p class="text-sm text-gray-500">
            绑定微信后，您将在收到新邮件时收到微信通知提醒。
          </p>
        </div>

        <!-- 内容区域 -->
        <div class="overflow-y-auto flex-1">
          <div v-if="loading" class="text-center py-4">
            <div class="inline-flex items-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              正在加载...
            </div>
          </div>

          <!-- 未绑定状态 -->
          <div v-else-if="!wechatInfo.is_bound" class="space-y-4 pb-4">
            <div class="text-center">
              <h4 class="text-md font-medium text-gray-900 mb-2">扫码绑定微信</h4>
              <p class="text-sm text-gray-600 mb-4">使用微信扫描下方二维码完成绑定</p>
              
              <!-- 二维码显示区域 -->
              <div v-if="qrCodeUrl" class="flex justify-center mb-4">
                <div class="p-3 sm:p-4 bg-white border-2 border-gray-200 rounded-lg">
                  <img :src="qrCodeUrl" alt="微信绑定二维码" class="w-40 h-40 sm:w-48 sm:h-48" />
                </div>
              </div>
              
              <!-- 二维码状态 -->
              <div class="text-sm text-gray-500 mb-4">
                <span v-if="bindingStatus === 'pending'">🔄 等待扫码...</span>
                <span v-else-if="bindingStatus === 'scanned'" class="text-blue-600">📱 已扫码，请在微信中确认</span>
                <span v-else-if="bindingStatus === 'bound'" class="text-green-600">✅ 绑定成功！</span>
                <span v-else-if="bindingStatus === 'expired'" class="text-red-600">⏰ 二维码已过期</span>
              </div>
              
              <!-- 操作按钮 -->
              <div class="flex flex-col sm:flex-row justify-center gap-2 sm:space-x-2 sm:gap-0">
                <button
                  @click="generateQRCode"
                  :disabled="generatingQR"
                  class="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {{ generatingQR ? '生成中...' : (qrCodeUrl ? '重新生成' : '生成二维码') }}
                </button>
                
                <button
                  v-if="qrCodeUrl && bindingStatus === 'pending'"
                  @click="checkBindingStatus"
                  :disabled="checkingStatus"
                  class="w-full sm:w-auto px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {{ checkingStatus ? '检查中...' : '检查状态' }}
                </button>
              </div>
            </div>
          </div>

          <!-- 已绑定状态 -->
          <div v-else class="space-y-4 pb-4">
            <div class="bg-green-50 border border-green-200 rounded-md p-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-green-800">微信已绑定</h3>
                  <div class="mt-2 text-sm text-green-700">
                    <p>OpenID: {{ wechatInfo.masked_openid }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 通知设置 -->
            <div class="space-y-3">
              <h4 class="text-sm font-medium text-gray-900">通知设置</h4>
              
              <!-- 启用通知开关 -->
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-700">启用邮件通知</span>
                <button
                  @click="toggleNotification"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                    notificationSettings.enable_notification ? 'bg-blue-600' : 'bg-gray-200'
                  ]"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 rounded-full bg-white transition-transform',
                      notificationSettings.enable_notification ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
              </div>

              <!-- 通知文件夹设置 -->
              <div v-if="notificationSettings.enable_notification">
                <label class="text-sm text-gray-700">通知文件夹</label>
                <div class="mt-2 space-y-2">
                  <label class="inline-flex items-center">
                    <input
                      type="checkbox"
                      value="inbox"
                      v-model="notificationSettings.notification_folders"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    >
                    <span class="ml-2 text-sm text-gray-700">收件箱</span>
                  </label>
                  <!-- 暂时只支持收件箱通知 -->
                </div>
              </div>

              <!-- 保存设置按钮 -->
              <div class="pt-2">
                <button
                  @click="saveNotificationSettings"
                  :disabled="savingSettings"
                  class="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {{ savingSettings ? '保存中...' : '保存设置' }}
                </button>
              </div>
            </div>

            <!-- 解绑按钮 -->
            <div class="pt-4 border-t border-gray-200">
              <button
                @click="unbindWechat"
                :disabled="unbinding"
                class="w-full px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-600 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {{ unbinding ? '解绑中...' : '解绑微信' }}
              </button>
            </div>

            <!-- 最近通知记录 -->
            <div v-if="wechatInfo.recent_notifications && wechatInfo.recent_notifications.length > 0" class="pt-4 border-t border-gray-200">
              <h4 class="text-sm font-medium text-gray-900 mb-2">最近通知</h4>
              <div class="space-y-2 max-h-32 overflow-y-auto">
                <div
                  v-for="notification in wechatInfo.recent_notifications"
                  :key="notification.id"
                  class="text-xs bg-gray-100 rounded p-2"
                >
                  <div class="flex justify-between">
                    <span :class="{
                      'text-green-600': notification.status === 'sent',
                      'text-red-600': notification.status === 'failed',
                      'text-yellow-600': notification.status === 'pending'
                    }">
                      {{ notification.status === 'sent' ? '✅ 已发送' : notification.status === 'failed' ? '❌ 失败' : '⏳ 等待' }}
                    </span>
                    <span class="text-gray-500">{{ formatDate(notification.created_at) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="pt-4 border-t border-gray-200 flex-shrink-0">
          <button
            @click="$emit('close')"
            class="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api from '@/utils/api'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close'])

// 响应式数据
const loading = ref(false)
const wechatInfo = ref({
  is_bound: false,
  masked_openid: '',
  notification_enabled: true,
  notification_folders: ['inbox'],
  recent_notifications: []
})

const notificationSettings = ref({
  enable_notification: true,
  notification_folders: ['inbox']
})

const qrCodeUrl = ref('')
const bindCode = ref('')
const bindingStatus = ref('') // pending, scanned, bound, expired
const generatingQR = ref(false)
const checkingStatus = ref(false)
const savingSettings = ref(false)
const unbinding = ref(false)

let statusCheckInterval = null

// 监听模态框显示状态
watch(() => props.show, (newShow) => {
  if (newShow) {
    loadWechatInfo()
  } else {
    // 关闭时清理定时器
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval)
      statusCheckInterval = null
    }
  }
})

// 生命周期
onMounted(() => {
  if (props.show) {
    loadWechatInfo()
  }
})

// 加载微信信息
const loadWechatInfo = async () => {
  loading.value = true
  try {
    const response = await api.get('/api/wechat/info/')
    if (response.data.success) {
      wechatInfo.value = response.data.data
      notificationSettings.value = {
        enable_notification: response.data.data.notification_enabled,
        notification_folders: response.data.data.notification_folders
      }
    }
  } catch (error) {
    console.error('加载微信信息失败:', error)
    showMessage('加载微信信息失败', 'error')
  } finally {
    loading.value = false
  }
}

// 生成二维码
const generateQRCode = async () => {
  generatingQR.value = true
  try {
    const response = await api.post('/api/wechat/generate-qrcode/')
    if (response.data.success) {
      const data = response.data.data
      qrCodeUrl.value = data.qr_code_url
      bindCode.value = data.bind_code
      bindingStatus.value = 'pending'
      
      // 开始轮询检查绑定状态
      startStatusCheck()
      
      showMessage('二维码生成成功，请使用微信扫码', 'success')
    } else {
      showMessage(response.data.message || '生成二维码失败', 'error')
    }
  } catch (error) {
    console.error('生成二维码失败:', error)
    showMessage('生成二维码失败', 'error')
  } finally {
    generatingQR.value = false
  }
}

// 检查绑定状态
const checkBindingStatus = async () => {
  if (!bindCode.value) return
  
  checkingStatus.value = true
  try {
    const response = await api.get('/api/wechat/check-binding/', {
      params: { bind_code: bindCode.value }
    })
    
    if (response.data.success) {
      bindingStatus.value = response.data.status
      
      if (response.data.status === 'bound') {
        // 绑定成功，重新加载信息
        showMessage('微信绑定成功！', 'success')
        await loadWechatInfo()
        stopStatusCheck()
      } else if (response.data.status === 'expired') {
        showMessage('二维码已过期，请重新生成', 'warning')
        stopStatusCheck()
      }
    } else {
      showMessage(response.data.message || '检查状态失败', 'error')
    }
  } catch (error) {
    console.error('检查绑定状态失败:', error)
    showMessage('检查状态失败', 'error')
  } finally {
    checkingStatus.value = false
  }
}

// 开始状态检查
const startStatusCheck = () => {
  stopStatusCheck() // 先停止之前的检查
  statusCheckInterval = setInterval(() => {
    checkBindingStatus()
  }, 3000) // 每3秒检查一次
}

// 停止状态检查
const stopStatusCheck = () => {
  if (statusCheckInterval) {
    clearInterval(statusCheckInterval)
    statusCheckInterval = null
  }
}

// 切换通知开关
const toggleNotification = () => {
  notificationSettings.value.enable_notification = !notificationSettings.value.enable_notification
}

// 保存通知设置
const saveNotificationSettings = async () => {
  savingSettings.value = true
  try {
    const response = await api.post('/api/wechat/settings/', notificationSettings.value)
    if (response.data.success) {
      showMessage('设置保存成功', 'success')
      // 更新本地信息
      wechatInfo.value.notification_enabled = notificationSettings.value.enable_notification
      wechatInfo.value.notification_folders = notificationSettings.value.notification_folders
    } else {
      showMessage(response.data.message || '保存设置失败', 'error')
    }
  } catch (error) {
    console.error('保存设置失败:', error)
    showMessage('保存设置失败', 'error')
  } finally {
    savingSettings.value = false
  }
}

// 解绑微信
const unbindWechat = async () => {
  if (!confirm('确定要解绑微信吗？解绑后将无法接收微信通知。')) {
    return
  }
  
  unbinding.value = true
  try {
    const response = await api.post('/api/wechat/unbind/')
    if (response.data.success) {
      showMessage('微信解绑成功', 'success')
      // 重置状态
      wechatInfo.value.is_bound = false
      wechatInfo.value.masked_openid = ''
      qrCodeUrl.value = ''
      bindCode.value = ''
      bindingStatus.value = ''
    } else {
      showMessage(response.data.message || '解绑失败', 'error')
    }
  } catch (error) {
    console.error('解绑失败:', error)
    showMessage('解绑失败', 'error')
  } finally {
    unbinding.value = false
  }
}

// 格式化日期
const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 显示消息（这里使用简单的alert，实际项目中可以使用toast组件）
const showMessage = (message, type = 'info') => {
  // 这里可以集成实际的消息提示组件
  if (type === 'error') {
    alert('❌ ' + message)
  } else if (type === 'success') {
    alert('✅ ' + message)
  } else if (type === 'warning') {
    alert('⚠️ ' + message)
  } else {
    alert('ℹ️ ' + message)
  }
}

// 清理资源
const cleanup = () => {
  stopStatusCheck()
}

// 组件卸载时清理
import { onUnmounted } from 'vue'
onUnmounted(() => {
  cleanup()
})
</script>
